import { useState, useMemo } from 'react';
import styled from 'styled-components';
import { s } from '../PeriodStatsPage.styles';
import { downloadAsExcel } from '../../../../utils/downloadAsExcel';
import { formatCurrency } from '../../../../utils/Function';

type WeeklyNPLTrendProps = {
  periodSummary: string;
  region: string;
  court?: string;
  usage: string;
  usageDetail?: string;
  fiMain: string;
  fiSub?: string;
  data: WeeklyTrendItem[];
  loading: boolean;
  error: string | null;
  initialVisibleCount?: number;
};

export type WeeklyTrendItem = {
  id: string;
  week: string; // "2025년 30주차"
  period: string; // "07/21~07/27"
  claimAmount: number; // 청구금액(원)
  bidRate: number; // 낙찰률(%)
  winPriceRate: number; // 낙찰가율(%)
  collateralValueRate: number; // 담보가치율(%)
  recoveryRate: number; // 회수율(%)
  lossRate: number; // 손실률(%)
  mortgageAmount: number; // 근저당액
  shortfallRate: number; // 회수부족율
};

export default function WeeklyNPLTrend({
  periodSummary,
  region,
  court,
  usage,
  usageDetail,
  fiMain,
  fiSub,
  data = [],
  loading = false,
  error = null,
  initialVisibleCount = 4,
}: WeeklyNPLTrendProps) {
  const [showAll, setShowAll] = useState(false);

  const handleDownloadExcel = () => {
    const rows = [...visible.map(buildRow), buildAvgRow()];
    downloadAsExcel(rows as any[], '주간_경매_동향');
  };

  // 정렬 관련 로직 삭제: data 그대로 사용
  const rows = useMemo(() => [...data], [data]);

  const visible = useMemo(
    () => rows.slice(0, showAll ? rows.length : initialVisibleCount),
    [rows, showAll, initialVisibleCount],
  );

  const avg = useMemo(() => {
    const n = data.length || 1;
    if (!data.length) {
      return {
        claimAmount: 0,
        bidRate: 0,
        winPriceRate: 0,
        collateralValueRate: 0,
        recoveryRate: 0,
        lossRate: 0,
      };
    }
    const sum = data.reduce(
      (a, c) => ({
        claimAmount: a.claimAmount + c.claimAmount,
        bidRate: a.bidRate + c.bidRate,
        winPriceRate: a.winPriceRate + c.winPriceRate,
        collateralValueRate: a.collateralValueRate + c.collateralValueRate,
        recoveryRate: a.recoveryRate + c.recoveryRate,
        lossRate: a.lossRate + c.lossRate,
      }),
      {
        claimAmount: 0,
        bidRate: 0,
        winPriceRate: 0,
        collateralValueRate: 0,
        recoveryRate: 0,
        lossRate: 0,
      },
    );

    return {
      claimAmount: Math.round(sum.claimAmount / n),
      bidRate: +(sum.bidRate / n).toFixed(1),
      winPriceRate: +(sum.winPriceRate / n).toFixed(1),
      collateralValueRate: +(sum.collateralValueRate / n).toFixed(1),
      recoveryRate: +(sum.recoveryRate / n).toFixed(1),
      lossRate: +(sum.lossRate / n).toFixed(1),
    };
  }, [data]);

  const buildRow = (r: WeeklyTrendItem) => ({
    주차: r.week,
    기간: r.period,
    청구금액: r.claimAmount.toLocaleString('ko-KR'),
    '낙찰률(%)': Number(r.bidRate.toFixed(1)),
    '낙찰가율(%)': Number(r.winPriceRate.toFixed(1)),
    '담보가치율(%)': Number(r.collateralValueRate.toFixed(1)),
    '회수율(%)': Number(r.recoveryRate.toFixed(1)),
    '손실률(%)': Number(r.lossRate.toFixed(1)),
  });

  const buildAvgRow = () => ({
    주차: '평균',
    기간: '-',
    청구금액: avg.claimAmount.toLocaleString('ko-KR'),
    '낙찰률(%)': Number(avg.bidRate.toFixed(1)),
    '낙찰가율(%)': Number(avg.winPriceRate.toFixed(1)),
    '담보가치율(%)': Number(avg.collateralValueRate.toFixed(1)),
    '회수율(%)': Number(avg.recoveryRate.toFixed(1)),
    '손실률(%)': Number(avg.lossRate.toFixed(1)),
  });

  return (
    <s.Wrap>
      <s.Header>
        <s.HeaderRow>
          <s.Img $bg="#e4edfd">
            <img src="/assets/images/icon/blue_clock.svg" alt="" />
          </s.Img>
          <s.ExcelButton onClick={handleDownloadExcel} aria-label="엑셀 저장">
            <img src="/assets/images/icon/download-black.svg" alt="" />
          </s.ExcelButton>
        </s.HeaderRow>
        <s.TitleBox>
          <h3>주간 NPL 동향</h3>
          <p>해당 기간 기준 4주~12주간 주요 경매 대상 NPL 지표</p>
          <s.SelectedOptionBox>
            <s.SelectedOption>{periodSummary}</s.SelectedOption>
            <s.SelectedOption>
              {court && court !== 'all' ? court : `${region} 지역`}
            </s.SelectedOption>
            <s.SelectedOption>
              {usage === '전체'
                ? `${usage} 용도`
                : `${usage} ${usageDetail ? ` ${usageDetail} 용도` : ''}`}
            </s.SelectedOption>
            <s.SelectedOption>
              {fiMain === '전체'
                ? `${fiMain} 금융기관`
                : `${fiSub ? ` ${fiSub}` : ''}`}
            </s.SelectedOption>
          </s.SelectedOptionBox>
        </s.TitleBox>
      </s.Header>

      {loading ? (
        <LoaderWrap>
          <LoaderImg
            src="/assets/images/icon/loader.gif"
            alt="주간 경매 동향"
          />
        </LoaderWrap>
      ) : (
        <>
          <s.TableWrap>
            <s.Table style={{ margin: '24px 0px' }}>
              <thead>
                <tr>
                  <s.Th width="204px">
                    <SortHeader style={{ justifyContent: 'flex-start' }}>
                      기간
                    </SortHeader>
                  </s.Th>
                  <s.Th width="140px" align="right">
                    <SortHeader>청구금액</SortHeader>
                  </s.Th>
                  <s.Th width="108px" align="right">
                    <SortHeader>낙찰률</SortHeader>
                  </s.Th>
                  <s.Th width="108px" align="right">
                    <SortHeader>낙찰가율</SortHeader>
                  </s.Th>
                  <s.Th width="108px" align="right">
                    <SortHeader>담보가치율</SortHeader>
                  </s.Th>
                  <s.Th width="108px" align="right">
                    <SortHeader>회수율</SortHeader>
                  </s.Th>
                  <s.Th width="108px" align="right">
                    <SortHeader>손실률</SortHeader>
                  </s.Th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <tr key={row.id}>
                    <s.Td align="left">
                      <PeriodBox>
                        <Week>{row.week}</Week>
                        {row.period}
                      </PeriodBox>
                    </s.Td>
                    <s.Td align="right">{formatCurrency(row.claimAmount)}</s.Td>
                    <s.Td align="right">{row.bidRate.toFixed(1)}%</s.Td>
                    <s.Td align="right">{row.winPriceRate.toFixed(1)}%</s.Td>
                    <s.Td align="right">
                      {row.collateralValueRate.toFixed(1)}%
                    </s.Td>
                    <s.Td align="right">{row.recoveryRate.toFixed(1)}%</s.Td>
                    <s.Td align="right">{row.lossRate.toFixed(1)}%</s.Td>
                  </tr>
                ))}
                <s.AvgTr>
                  <s.TdAvg align="left" style={{ fontWeight: 500 }}>
                    평균
                  </s.TdAvg>
                  <s.TdAvg align="right">
                    {formatCurrency(avg.claimAmount)}
                  </s.TdAvg>
                  <s.TdAvg align="right">{avg.bidRate.toFixed(1)}%</s.TdAvg>
                  <s.TdAvg align="right">
                    {avg.winPriceRate.toFixed(1)}%
                  </s.TdAvg>
                  <s.TdAvg align="right">
                    {avg.collateralValueRate.toFixed(1)}%
                  </s.TdAvg>
                  <s.TdAvg align="right">
                    {avg.recoveryRate.toFixed(1)}%
                  </s.TdAvg>
                  <s.TdAvg align="right">{avg.lossRate.toFixed(1)}%</s.TdAvg>
                </s.AvgTr>
              </tbody>
            </s.Table>
          </s.TableWrap>

          {data.length > 4 && (
            <MoreButton onClick={() => setShowAll((v) => !v)}>
              <span>{showAll ? '접기' : '더보기'}</span>
              <img
                src="/assets/images/arrow/arrow-down-gray.svg"
                alt=""
                style={{
                  transform: showAll ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </MoreButton>
          )}
        </>
      )}
    </s.Wrap>
  );
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 40px;
`;

const PeriodBox = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const Week = styled.span`
  color: #787d87;
`;

const MoreButton = styled.div`
  width: 100%;
  display: flex;
  gap: 6px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-weight: 500;
  color: rgba(23, 30, 38, 0.76);
  border-radius: 6px;
  cursor: pointer;

  img {
    width: 16px;
    height: 16px;
    transition: transform 0.15s ease;
  }
`;

const LoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  width: 100%;
`;

const LoaderImg = styled.img`
  width: 24px;
  height: 24px;
`;

const SortHeader = styled.button`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  cursor: default; /* 클릭 불가처럼 보이게 */
  background: transparent;
  border: 0;
  padding: 0;
`;
